/**
 * EntityPropertyTable
 *
 * Generic table rendering all predicate–object pairs returned by a
 * DESCRIBE query. Values are shown with friendly names by default,
 * while prefix view can be enabled via toggle. Internal URIs link
 * to their dereferencing page.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Switch } from '@bn-apps/design-system-ui-react';
import { shortenUri } from '@hooks/useEntity';
import { useResolvedDisplayValues } from '@hooks/useResolvedDisplayValues';
import type { ResolvedDisplayValues } from '@hooks/useResolvedDisplayValues';
import { uriToDereferencePath } from '@utils/prefix.utils';
import { humanizeUriLocalName } from '@utils/uriDisplay.utils';
import { getIntlLocale } from '@utils/locale.utils';
import type { PrefixMap } from '@utils/prefix.utils';
import type { EntityProperty, EntityObject } from '@/types';
import styles from './EntityPage.module.scss';

const COLLAPSE_THRESHOLD = 3;
const DISPLAY_VALUE_SEPARATOR = ' / ';

interface EntityPropertyTableProps {
  properties: EntityProperty[];
  prefixes: PrefixMap;
  showUris: boolean;
  onShowUrisChange: (value: boolean) => void;
}

const XSD_NS = 'http://www.w3.org/2001/XMLSchema#';
const XSD_ANY_URI = `${XSD_NS}anyURI`;
const XSD_DATE_TIME = `${XSD_NS}dateTime`;
const XSD_DATE = `${XSD_NS}date`;
const XSD_BOOLEAN = `${XSD_NS}boolean`;
const XSD_NUMERIC = new Set([`${XSD_NS}decimal`, `${XSD_NS}double`, `${XSD_NS}float`, `${XSD_NS}integer`]);

function isUriValued(obj: EntityObject): boolean {
  return obj.type === 'uri' || (obj.type === 'literal' && obj.datatype === XSD_ANY_URI);
}

function displayText(texts: string[] | undefined): string | null {
  return texts && texts.length > 0 ? texts.join(DISPLAY_VALUE_SEPARATOR) : null;
}

function formatLiteralValue(
  obj: EntityObject,
  locale: string,
  yesLabel: string,
  noLabel: string
): string | null {
  if (!obj.datatype) return null;

  if (obj.datatype === XSD_DATE_TIME || obj.datatype === XSD_DATE) {
    const date = new Date(obj.value);
    if (isNaN(date.getTime())) return null;

    return obj.datatype === XSD_DATE
      ? new Intl.DateTimeFormat(locale, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          timeZone: 'UTC',
        }).format(date)
      : new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
  }

  if (obj.datatype === XSD_BOOLEAN) {
    return obj.value === 'true' ? yesLabel : noLabel;
  }

  if (XSD_NUMERIC.has(obj.datatype)) {
    const num = Number(obj.value);
    if (isNaN(num)) return null;
    return new Intl.NumberFormat(locale).format(num);
  }

  return null;
}

/**
 * Render a single RDF object value.
 */
function ObjectValue({
  obj,
  prefixes,
  resolvedValues,
  showFriendlyNames,
}: {
  obj: EntityObject;
  prefixes: PrefixMap;
  resolvedValues: ResolvedDisplayValues;
  showFriendlyNames: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation();

  if (isUriValued(obj)) {
    const shortened = shortenUri(obj.value, prefixes);
    const dereferencePath = uriToDereferencePath(obj.value);
    const resolved = showFriendlyNames ? displayText(resolvedValues[obj.value]) : null;
    const displayValue = showFriendlyNames ? (resolved ?? shortened) : shortened;

    return (
      <Link to={dereferencePath} className={styles.internalLink} title={obj.value}>
        <span className={styles.friendlyLabel}>{displayValue}</span>
      </Link>
    );
  }

  // Literal
  const locale = getIntlLocale(i18n.language);
  const formatted = showFriendlyNames
    ? formatLiteralValue(obj, locale, t('entity.boolean.yes'), t('entity.boolean.no'))
    : null;

  return (
    <span className={styles.literalValue}>
      {formatted ?? obj.value}
      {obj.lang && <span className={styles.langTag}>@{obj.lang}</span>}
      {obj.datatype && !obj.lang && !formatted && (
        <span className={styles.datatypeTag}>
          ({shortenUri(obj.datatype, prefixes)})
        </span>
      )}
    </span>
  );
}

function CollapsibleObjectList({
  objects,
  prefixes,
  resolvedValues,
  showFriendlyNames,
}: {
  objects: EntityObject[];
  prefixes: PrefixMap;
  resolvedValues: ResolvedDisplayValues;
  showFriendlyNames: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? objects : objects.slice(0, COLLAPSE_THRESHOLD);
  const hidden = objects.length - COLLAPSE_THRESHOLD;

  return (
    <ul className={styles.objectList}>
      {visible.map((obj, idx) => (
        <li key={`${obj.value}-${idx}`} className={styles.objectItem}>
          <ObjectValue
            obj={obj}
            prefixes={prefixes}
            resolvedValues={resolvedValues}
            showFriendlyNames={showFriendlyNames}
          />
        </li>
      ))}
      {!expanded && hidden > 0 && (
        <li className={styles.objectItem}>
          <button
            type="button"
            className={styles.showMoreButton}
            onClick={() => setExpanded(true)}
          >
            {t('entity.showMore', { count: hidden })}
          </button>
        </li>
      )}
      {expanded && hidden > 0 && (
        <li className={styles.objectItem}>
          <button
            type="button"
            className={styles.showMoreButton}
            onClick={() => setExpanded(false)}
          >
            {t('entity.showLess')}
          </button>
        </li>
      )}
    </ul>
  );
}

const PREFIX_ORDER = ['rdf', 'rdfs', 'dct', 'rtm', 'railml3', 'time', 'bno', 'era'];

function getPrefixRank(shortened: string): number {
  const prefix = shortened.includes(':') ? shortened.split(':')[0] : '';
  const idx = PREFIX_ORDER.indexOf(prefix);
  return idx === -1 ? PREFIX_ORDER.length : idx;
}

function getDisplayPrefix(shortenedUri: string): string | null {
  const separatorIndex = shortenedUri.indexOf(':');
  return separatorIndex > 0 ? shortenedUri.slice(0, separatorIndex) : null;
}

function sortProperties(properties: EntityProperty[], prefixes: PrefixMap): EntityProperty[] {
  return [...properties].sort((a, b) => {
    const sa = shortenUri(a.predicate, prefixes);
    const sb = shortenUri(b.predicate, prefixes);
    const rankDiff = getPrefixRank(sa) - getPrefixRank(sb);
    if (rankDiff !== 0) return rankDiff;
    return sa.localeCompare(sb);
  });
}

const EntityPropertyTable = ({
  properties,
  prefixes,
  showUris,
  onShowUrisChange,
}: EntityPropertyTableProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const showFriendlyNames = !showUris;

  // Collect every predicate and URI-valued object across the entity's
  // properties so display values can be resolved in a single batched query.
  // Empty in prefix mode, which suppresses the query entirely.
  const allUris = useMemo(() => {
    if (!showFriendlyNames) return [];
    const uris: string[] = [];
    for (const prop of properties) {
      uris.push(prop.predicate);
      for (const obj of prop.objects) {
        if (isUriValued(obj)) uris.push(obj.value);
      }
    }
    return uris;
  }, [properties, showFriendlyNames]);

  const resolvedValues = useResolvedDisplayValues(allUris, i18n.language);

  const sortedProperties = useMemo(
    () => sortProperties(properties, prefixes),
    [properties, prefixes]
  );

  if (properties.length === 0) {
    return (
      <div className={styles.emptyState}>
        {t('entity.noProperties')}
      </div>
    );
  }

  return (
    <div className={styles.propertyTableWrapper}>
      <div className={styles.propertyTableToolbar}>
        <label className={styles.uriToggle}>
          <Switch
            checked={showUris}
            onChange={(e) => onShowUrisChange(e.target.checked)}
          />
          {t('entity.showUris')}
        </label>
      </div>
      <table className={styles.propertyTable} aria-label={t('entity.propertyTableCaption')}>
        <thead>
          <tr>
            <th scope="col">{t('entity.predicate')}</th>
            <th scope="col">{t('entity.value')}</th>
          </tr>
        </thead>
        <tbody>
          {sortedProperties.map((prop) => {
            const shortenedPredicate = shortenUri(prop.predicate, prefixes);
            const predicatePath = uriToDereferencePath(prop.predicate);
            const predicateResolved = showFriendlyNames
              ? displayText(resolvedValues[prop.predicate])
              : null;
            const predicateHumanized = showFriendlyNames
              ? humanizeUriLocalName(prop.predicate)
              : null;
            const friendlyPredicate = predicateResolved ?? predicateHumanized;
            const predicatePrefix = getDisplayPrefix(shortenedPredicate);

            return (
              <tr key={prop.predicate}>
                <td className={styles.predicateCell}>
                  <Link
                    to={predicatePath}
                    className={styles.internalLink}
                    title={prop.predicate}
                  >
                    <span className={styles.friendlyLabel}>
                      {showFriendlyNames && friendlyPredicate ? (
                        <>
                          {friendlyPredicate}
                          {predicatePrefix && (
                            <span className={styles.predicatePrefix}>{predicatePrefix}</span>
                          )}
                        </>
                      ) : (
                        shortenedPredicate
                      )}
                    </span>
                  </Link>
                </td>
                <td>
                  {prop.objects.length === 1 ? (
                    <ObjectValue
                      obj={prop.objects[0]}
                      prefixes={prefixes}
                      resolvedValues={resolvedValues}
                      showFriendlyNames={showFriendlyNames}
                    />
                  ) : (
                    <CollapsibleObjectList
                      objects={prop.objects}
                      prefixes={prefixes}
                      resolvedValues={resolvedValues}
                      showFriendlyNames={showFriendlyNames}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default EntityPropertyTable;